import { PlusCircle, Eye } from "react-feather";
import { Link } from "react-router-dom";
import { DatePicker, Modal, Table, Input } from "antd";
import React, { useState, useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import moment from "moment";
import Swal from "sweetalert2";
import UpdateTrainingProgress from "../../core/modals/training/UpdateTrainingProgress";
import { getAllProgress, getAssignmentsByUserId } from "../../service/trainingService";

const TrainingProgressList = () => {
  const [openUpdateProgressModal, setOpenUpdateProgressModal] = useState(false);
  const [openViewAssignmentsModal, setOpenViewAssignmentsModal] = useState(false);
  const [selectedProgress, setSelectedProgress] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [progressRecords, setProgressRecords] = useState([]);
  const [userAssignments, setUserAssignments] = useState([]);
  const [searchEmployeeName, setSearchEmployeeName] = useState("");
  const [searchProgramName, setSearchProgramName] = useState("");
  const [searchCompletionDate, setSearchCompletionDate] = useState(null);
  const [paginationProgress, setPaginationProgress] = useState({ current: 1, pageSize: 5, total: 0 });
  const [paginationAssignments, setPaginationAssignments] = useState({ current: 1, pageSize: 5, total: 0 });
  const [loading, setLoading] = useState(false);
  const pageSizeOptions = [5, 10, 20, 50, 100];

  const getUserId = () => {
    try {
      const storedUserData = localStorage.getItem('user');
      if (!storedUserData) {
        console.error('User data not found in local storage.');
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'User data not found. Please log in again.',
        });
        return null;
      }
      const user = JSON.parse(storedUserData);
      return user.data._id; // Adjust based on your localStorage structure
    } catch (error) {
      console.error('Error parsing user data:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to retrieve user data.',
      });
      return null;
    }
  };

  const userId = getUserId();

  useEffect(() => {
    if (userId) {
      fetchProgress();
    }
  }, [userId, paginationProgress.current, paginationProgress.pageSize]);

  useEffect(() => {
    if (selectedUserId) {
      fetchUserAssignments(selectedUserId);
    }
  }, [selectedUserId, paginationAssignments.current, paginationAssignments.pageSize]);

  const fetchProgress = async () => {
    setLoading(true);
    try {
      const data = await getAllProgress({
        employeeName: searchEmployeeName,
        programName: searchProgramName,
        completionDate: searchCompletionDate ? moment(searchCompletionDate).format('YYYY-MM-DD') : '',
      });
      setProgressRecords(data);
      setPaginationProgress((prev) => ({ ...prev, total: data.length }));
    } catch (error) {
      console.error("Error fetching progress:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to fetch progress records.',
      });
      setProgressRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchProgress = async () => {
    if (!userId) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'User not logged in.',
      });
      return;
    }
    setPaginationProgress((prev) => ({ ...prev, current: 1 }));
    fetchProgress();
  };

  const fetchUserAssignments = async (userId) => {
    setLoading(true);
    try {
      const data = await getAssignmentsByUserId(userId);
      setUserAssignments(data);
      setPaginationAssignments((prev) => ({ ...prev, total: data.length }));
    } catch (error) {
      console.error("Error fetching user assignments:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to fetch user assignments.',
      });
      setUserAssignments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination, type) => {
    if (type === "progress") {
      setPaginationProgress(pagination);
    } else if (type === "assignments") {
      setPaginationAssignments(pagination);
    }
  };

  const columnsProgress = [
    {
      title: "Employee Name",
      dataIndex: ["assignment", "employee"],
      key: "employee",
      render: (employee) => employee?.full_name || "N/A",
    },
    {
      title: "Training Program",
      dataIndex: ["assignment", "trainingProgram"],
      key: "trainingProgram",
      render: (program) => program?.programName || "N/A",
    },
    {
      title: "Progress (%)",
      dataIndex: "progress",
      key: "progress",
    },
    {
      title: "Assessment Score",
      dataIndex: "assessmentScore",
      key: "assessmentScore",
      render: (score) => score || "N/A",
    },
    {
      title: "Certificate Earned",
      dataIndex: "certificateEarned",
      key: "certificateEarned",
      render: (earned) => (earned ? "Yes" : "No"),
    },
    {
      title: "Completion Date",
      dataIndex: "completionDate",
      key: "completionDate",
      render: (date) => (date ? moment(date).format("YYYY-MM-DD") : "N/A"),
    },
    {
      title: "Action",
      key: "action",
      render: (record) => (
        <td className="action-table-data">
          <Link
            className="me-2 p-2"
            to="#"
            onClick={() => {
              setSelectedProgress(record);
              setOpenUpdateProgressModal(true);
            }}
            title="Edit progress"
          >
            <i data-feather="edit" className="feather-edit"></i>
          </Link>
          <Link
            className="me-2 p-2"
            to="#"
            onClick={() => {
              const userId = record.assignment?.employee?.user;
              if (userId) {
                setSelectedUserId(userId);
                setOpenViewAssignmentsModal(true);
              } else {
                Swal.fire({
                  icon: 'error',
                  title: 'Error',
                  text: 'No user ID found for this employee.',
                });
              }
            }}
            title="View assignments"
          >
            <Eye className="feather-eye" />
          </Link>
        </td>
      ),
    },
  ];

  const columnsAssignments = [
    {
      title: "Employee Name",
      dataIndex: "employee",
      key: "employee",
      render: (employee) => employee?.full_name || "N/A",
    },
    {
      title: "Training Program",
      dataIndex: "trainingProgram",
      key: "trainingProgram",
      render: (program) => program?.programName || "N/A",
    },
    {
      title: "Assignment Date",
      dataIndex: "assignmentDate",
      key: "assignmentDate",
      render: (date) => (date ? moment(date).format("YYYY-MM-DD") : "N/A"),
    },
    {
      title: "Due Date",
      dataIndex: "dueDate",
      key: "dueDate",
      render: (date) => (date ? moment(date).format("YYYY-MM-DD") : "N/A"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
    },
  ];

  if (!userId) {
    return (
      <div className="page-wrapper-lt">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="header-leave-container d-flex align-items-center">
                <div>
                  <ArrowLeft size={32} />
                </div>
                <div className="page-title">
                  <h4>Training Progress</h4>
                  <h6>Please log in to view your progress</h6>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper-lt">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="header-leave-container d-flex align-items-center">
              <div>
                <ArrowLeft size={32} />
              </div>
              <div className="page-title">
                <h4>Training Progress</h4>
                <h6>Manage training progress</h6>
              </div>
            </div>
            <div className="page-btn">
              <a
                className="btn btn-added-grid"
                onClick={() => {
                  setSelectedProgress(null);
                  setOpenUpdateProgressModal(true);
                }}
                title="Create new progress"
              >
                <PlusCircle className="me-2" />
                Progress
              </a>
            </div>
          </div>
        </div>
        <div className="card table-list-card-grid">
          <div className="card-body" style={{ maxWidth: "100%", marginRight: "20px" }}>
            <div
              className="container mt-3"
              style={{ maxWidth: "100%", marginLeft: "20px", marginRight: "40px" }}
            >
              <div className="row d-flex flex-wrap align-items-start justify-content-between">
                <div className="col-12 col-md-3 text-start">
                  <label className="form-label">Employee Name</label>
                  <Input
                    value={searchEmployeeName}
                    onChange={(e) => setSearchEmployeeName(e.target.value)}
                    placeholder="Enter employee name"
                    style={{ width: "80%", height: "40px" }}
                  />
                </div>
                <div className="col-12 col-md-3 text-start">
                  <label className="form-label">Training Program</label>
                  <Input
                    value={searchProgramName}
                    onChange={(e) => setSearchProgramName(e.target.value)}
                    placeholder="Enter training program"
                    style={{ width: "80%", height: "40px" }}
                  />
                </div>
                <div className="col-12 col-md-3 text-start">
                  <label className="form-label">Completion Date</label>
                  <DatePicker
                    className="form-control"
                    placeholder="Select date"
                    value={searchCompletionDate}
                    onChange={(date) => setSearchCompletionDate(date)}
                    style={{ width: "80%", height: "40px" }}
                    format="YYYY-MM-DD"
                  />
                </div>
                <div className="col-12 col-md-3 text-start">
                  <button
                    disabled={loading}
                    className="btn btn-submit"
                    onClick={handleSearchProgress}
                    style={{ height: "38px", width: "55%" }}
                  >
                    {loading ? "Searching..." : "Search"}
                  </button>
                  {loading && (
                    <div className="spinner">
                      <div className="loading-circle"></div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <Table
                loading={loading}
                columns={columnsProgress}
                dataSource={progressRecords}
                rowKey="_id"
                pagination={{
                  current: paginationProgress.current,
                  pageSize: paginationProgress.pageSize,
                  pageSizeOptions: pageSizeOptions,
                  total: paginationProgress.total,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total) => `Total ${total} items`,
                }}
                onChange={(pagination) => handleTableChange(pagination, "progress")}
              />
            </div>
          </div>
        </div>
      </div>

      <Modal
        zIndex={200000}
        open={openUpdateProgressModal}
        onCancel={() => setOpenUpdateProgressModal(false)}
        footer={null}
        maskClosable={false}
        closable={false}
      >
        <UpdateTrainingProgress
          selectedProgress={selectedProgress}
          handleCancel={() => {
            setSelectedProgress(null);
            setOpenUpdateProgressModal(false);
          }}
          handleReload={fetchProgress}
        />
      </Modal>

      <Modal
        zIndex={200000}
        title="Assigned Trainings"
        open={openViewAssignmentsModal}
        onCancel={() => {
          setOpenViewAssignmentsModal(false);
          setSelectedUserId(null);
          setUserAssignments([]);
        }}
        footer={null}
        width={800}
      >
        <div className="card-body">
          <div className="table-responsive">
            <Table
              loading={loading}
              columns={columnsAssignments}
              dataSource={userAssignments}
              rowKey="_id"
              pagination={{
                current: paginationAssignments.current,
                pageSize: paginationAssignments.pageSize,
                pageSizeOptions: pageSizeOptions,
                total: paginationAssignments.total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `Total ${total} items`,
              }}
              onChange={(pagination) => handleTableChange(pagination, "assignments")}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TrainingProgressList;